import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import MaskedInput from 'react-text-mask';
import { FaSpinner, FaLink } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  adicionarAcompanhanteCheckinEventoRsvp,
  buscarCheckinEventoRsvp,
} from '~/Services/Service';
import * as helpers from '~/Utils/Helpers';

const touchStyle = {
  minHeight: 44,
  fontSize: '1rem',
};

const extrairMensagemErro = (err, fallback) => {
  if (err && err.message) return err.message;
  if (typeof err === 'string') return err;
  return fallback;
};

const validationSchema = Yup.object().shape({
  nome: Yup.string()
    .required('Informe o nome')
    .min(2, 'Nome muito curto'),
  telefone: Yup.string()
    .required('Informe o celular')
    .test('celular', 'Celular inválido', (val) => {
      const digits = val ? val.replace(/\D/g, '') : '';
      return /^[1-9]{2}9[0-9]{8}$/.test(digits);
    }),
  idade: Yup.number()
    .typeError('Idade inválida')
    .required('Informe a idade')
    .min(0, 'Idade inválida')
    .max(120, 'Idade inválida'),
});

const ModalAdicionarAcompanhante = ({
  show,
  onHide,
  codigoConfirmacao,
  nomeCliente,
  onCriado,
  onPromoverProspecto,
}) => {
  const [salvando, setSalvando] = useState(false);
  // "Amigo de outro empreendimento": prospecto existente localizado pelo telefone.
  const [amigo, setAmigo] = useState(null);
  const [verificando, setVerificando] = useState(false);

  // Ao digitar o celular, checa se já é um prospecto fora do evento. Se for,
  // oferecemos vincular ao evento + entrada em vez de criar acompanhante avulso.
  const verificarTelefone = async (telefone) => {
    const digits = (telefone || '').replace(/\D/g, '');
    if (digits.length !== 11) {
      setAmigo(null);
      return;
    }
    setVerificando(true);
    try {
      const resp = await buscarCheckinEventoRsvp({
        tipo: 'telefone',
        filtro: digits,
      });
      const achado =
        resp && resp.prospectos && resp.prospectos.length > 0
          ? resp.prospectos[0]
          : null;
      setAmigo(achado);
    } catch (err) {
      setAmigo(null);
    } finally {
      setVerificando(false);
    }
  };

  const salvar = async (values) => {
    setSalvando(true);
    try {
      const acompanhante = await adicionarAcompanhanteCheckinEventoRsvp({
        codigo_confirmacao: codigoConfirmacao,
        nome: values.nome.trim(),
        telefone: values.telefone.replace(/\D/g, ''),
        idade: parseInt(values.idade, 10),
      });
      toast.success('Acompanhante adicionado.');
      if (onCriado) onCriado(acompanhante);
    } catch (err) {
      toast.error(extrairMensagemErro(err, 'Erro ao adicionar acompanhante.'));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal backdrop="static" show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Adicionar acompanhante</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {nomeCliente && (
          <p className="text-muted mb-3">
            Vinculado a <strong>{nomeCliente}</strong>
          </p>
        )}
        <Formik
          initialValues={{ nome: '', telefone: '', idade: '' }}
          validationSchema={validationSchema}
          onSubmit={salvar}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            setFieldValue,
            handleSubmit,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Form.Group controlId="nome">
                <Form.Label>Nome</Form.Label>
                <Form.Control
                  type="text"
                  name="nome"
                  maxLength="100"
                  onChange={(e) => setFieldValue('nome', e.target.value)}
                  onBlur={handleBlur}
                  value={values.nome}
                  isInvalid={errors.nome && touched.nome}
                  disabled={salvando}
                  style={touchStyle}
                  autoFocus
                />
                <Form.Control.Feedback type="invalid">
                  {errors.nome}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="telefone">
                <Form.Label>Celular</Form.Label>
                <MaskedInput
                  type="tel"
                  className={
                    'form-control' +
                    (errors.telefone && touched.telefone ? ' is-invalid' : '')
                  }
                  name="telefone"
                  onChange={(e) => {
                    setFieldValue('telefone', e.target.value);
                    verificarTelefone(e.target.value);
                  }}
                  onBlur={handleBlur}
                  value={values.telefone}
                  mask={helpers.mask.celular}
                  placeholder="(00) 00000-0000"
                  disabled={salvando}
                  style={touchStyle}
                />
                {errors.telefone && touched.telefone && (
                  <small className="form-text text-danger">
                    {errors.telefone}
                  </small>
                )}
                {verificando && (
                  <small className="form-text text-muted">
                    <FaSpinner className="fa-spin mr-1" /> Verificando telefone...
                  </small>
                )}
              </Form.Group>

              {amigo && onPromoverProspecto && (
                <Alert variant="info" className="py-2 px-3">
                  <div className="mb-2" style={{ fontSize: '0.9rem' }}>
                    Esse telefone já é de <strong>{amigo.nome}</strong> (prospecto{' '}
                    {amigo.codigo_prospecto}), de outro cadastro. Vincular ao evento e
                    dar entrada como confirmado?
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={salvando}
                    onClick={() => onPromoverProspecto(amigo.codigo_prospecto)}
                  >
                    <FaLink className="mr-1" /> Vincular {amigo.nome} ao evento + entrada
                  </Button>
                </Alert>
              )}

              <Form.Group controlId="idade">
                <Form.Label>Idade</Form.Label>
                <Form.Control
                  type="number"
                  name="idade"
                  min="0"
                  max="120"
                  onChange={(e) => setFieldValue('idade', e.target.value)}
                  onBlur={handleBlur}
                  value={values.idade}
                  isInvalid={errors.idade && touched.idade}
                  disabled={salvando}
                  style={touchStyle}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.idade}
                </Form.Control.Feedback>
              </Form.Group>

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={onHide}
                  disabled={salvando}
                  style={{ ...touchStyle, padding: '0.5rem 1.5rem' }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={salvando}
                  style={{
                    ...touchStyle,
                    padding: '0.5rem 1.5rem',
                    fontWeight: 600,
                  }}
                >
                  {salvando ? (
                    <span>
                      <FaSpinner className="fa-spin" /> Salvando...
                    </span>
                  ) : (
                    'Salvar'
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};

export default ModalAdicionarAcompanhante;
